import { useEffect } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import InfoIcon from "../../../components/Icons/InfoIcon";
import KiloNewton from "../../../components/Math/Units/Metric/KiloNewton";
import KiloNewtonDividedByMetre from "../../../components/Math/Units/Metric/KiloNewtonDividedByMetre";
import KiloNewtonDividedByMetreSquared from "../../../components/Math/Units/Metric/KiloNewtonDividedByMetreSquared";
import Metre from "../../../components/Math/Units/Metric/Metre";
import Millimetre from "../../../components/Math/Units/Metric/Millimetre";
import AlertBox from "../../../components/Messages";
import { NumberFormatInfInput } from "../../../components/NumberFormatInfInput";
import { NumberFormatText } from "../../../components/NumberFormatText";
import { berechneSchichten } from "../../../services/Schichten/berechneSchichten";
import {
  addSchicht,
  deleteSchicht,
  selectAeussere,
  selectMinimalePfahllaenge,
  selectModelFaktor,
  selectPfahllaengeGewaehltInBodenIsCustom,
  selectPfahlneigungZurVertikalen,
  selectSchichtenCalculated,
  selectSchichteninput,
  selectTeilsicherheitsbeiwert,
  setBemessungspfahlwiderstand,
  setLreq,
  setPfahllaengeGewaehltInBoden,
  setSchichtenCalculated,
  setSumRsdi,
  updateSchichtenValue,
} from "../../../store/slices/aeussereSlice.js";
import { selectBemessungslast } from "../../../store/slices/bemessungslastSlice";
import { selectBohrkronendurchmesser } from "../../../store/slices/bohrkronendurchmesserSlice";

function AussereTable() {
  const { t } = useTranslation("translation");
  const dispatch = useDispatch();

  const minimalePfahllaenge = useSelector(selectMinimalePfahllaenge);
  const aussereData = useSelector(selectAeussere);
  const bemessungslast = useSelector(selectBemessungslast);
  const bohrkronendurchmesser = useSelector(selectBohrkronendurchmesser);
  const pfahlneigungZurVertikalen = useSelector(selectPfahlneigungZurVertikalen);
  const partialSafetyfactor = useSelector(selectTeilsicherheitsbeiwert);
  const modelFaktor = useSelector(selectModelFaktor);
  //HOTFIX: if there is no schichtenCalculated
  const test = aussereData.schichtenCalculated !== undefined ? true : false;

  function addNewRowHandler() {
    let rows = Object.keys(aussereData?.schichtenInput).length;

    let prevLayerFromValue = 0;
    if (Number(rows) >= 1) {
      let rowIndex = rows - 1;
      let prevLayer = aussereData?.schichtenInput[rowIndex];
      prevLayerFromValue = prevLayer.bis;
    }

    let newLayer = {
      index: rows,
      schichtName: `${t("aeussre.layer")} ${rows + 1}`,
      von: prevLayerFromValue,
      bis: Number.MAX_SAFE_INTEGER,
      qski: Number.NaN,
      a: 20,
    };

    dispatch(addSchicht(newLayer));
  }

  const notEnoughLayer = () =>
    Number(aussereData?.sumRsdi?.toFixed(2)) < Number(bemessungslast?.toFixed(2));

  useEffect(() => {
    const result = berechneSchichten({
      schichtenInput: aussereData?.schichtenInput,
      bohrkronendurchmesser: bohrkronendurchmesser,
      teilsicherheitsbeiwert: partialSafetyfactor,
      modellfaktor: modelFaktor,
      pfahlneigungZurVertikalen: pfahlneigungZurVertikalen,
      bemessungslast: Number(bemessungslast),
    });
    dispatch(setSchichtenCalculated(result.schichten));
    dispatch(setLreq(result.resultSumLi));
    dispatch(setSumRsdi(result.resultSumRsdi));
    dispatch(setBemessungspfahlwiderstand(result.bemessungspfahlwiderstand));
  }, [
    aussereData?.schichtenInput,
    bemessungslast,
    bohrkronendurchmesser,
    modelFaktor,
    partialSafetyfactor,
    pfahlneigungZurVertikalen,
  ]);

  const pfahllaengeGewaehltInBodenIsCustom = useSelector(selectPfahllaengeGewaehltInBodenIsCustom);
  useEffect(() => {
    if (!pfahllaengeGewaehltInBodenIsCustom) {
      dispatch(setPfahllaengeGewaehltInBoden(minimalePfahllaenge));
    }
  }, [minimalePfahllaenge, pfahllaengeGewaehltInBodenIsCustom]);

  return (
    <Row>
      <Col md={10}>
        <table className="table">
          <thead>
            <tr>
              <th scope="col">
                <p>{t("aeussre.tableLayer")}</p>
                <p>{t("aeussre.tableLayerUnit")}</p>
              </th>
              <th scope="col">
                <p>{t("aeussre.tableFrom")}</p>
                <p>
                  [<Metre />]
                </p>
              </th>
              <th scope="col">
                <p>{t("aeussre.tableTo")}</p>
                <p>
                  [<Metre />]
                </p>
              </th>
              <th scope="col">
                <p data-tooltip-id="MantelReibung">
                  <InfoIcon size={15} />
                </p>
                <Tooltip
                  id="MantelReibung"
                  className="formulaTooltip"
                  border="1px solid #0080c5"
                  place="right-start"
                  closeDelay={1000}
                  style={{ backgroundColor: "white", color: "#5F4B8BFF" }}
                >
                  <p>{t("aeussre.tableInfoMantelreibung")}</p>
                </Tooltip>
                <p>
                  q<sub>s,k</sub>
                </p>
                <p>
                  [<KiloNewtonDividedByMetreSquared />]
                </p>
              </th>
              <th>
                <p data-tooltip-id="Aufweitung">
                  <InfoIcon size={15} />
                </p>
                <Tooltip
                  id="Aufweitung"
                  className="formulaTooltip"
                  border="1px solid #0080c5"
                  place="right-start"
                  closeDelay={1000}
                  style={{ backgroundColor: "white", color: "#5F4B8BFF" }}
                >
                  <p>{t("aeussre.tableInfoAufweitung")}</p>
                </Tooltip>
                <p>a</p>
                <p>
                  [<Millimetre />]
                </p>
              </th>
              <th scope="col">
                <p>
                  R<sub>d,i</sub>
                </p>
                <p>
                  [<KiloNewtonDividedByMetre />]
                </p>
              </th>
              <th scope="col">
                <p>
                  L<sub>i,&beta;</sub>
                </p>
                <p>
                  [<Metre />]
                </p>
              </th>
              <th scope="col">
                <p>
                  R<sub>s,d,i</sub>
                </p>
                <p>
                  [<KiloNewton />]
                </p>
              </th>
              <th scope="col">{/* <p>{t("aeussre.tableActions")}</p> */}</th>
            </tr>
          </thead>
          <tbody>
            {test &&
              aussereData?.schichtenInput.map((_row, index) => {
                return <AussereTableRow key={index} index={index} />;
              })}
            {/* Results: */}
            <tr>
              <td colSpan="6">{/* 6x Empty */}</td>
              <td>
                <span className="smallInput">
                  <NumberFormatText value={aussereData?.lreq} decimalScale={2} />
                </span>
              </td>
              <td>
                <span className="smallInput">
                  <NumberFormatText value={aussereData?.sumRsdi} decimalScale={2} />
                </span>
              </td>
              <td>{/* Empty */}</td>
            </tr>
          </tbody>
        </table>
        <Button variant="success" size="sm" onClick={addNewRowHandler}>
          {t("aeussre.addLayerButton")}
        </Button>
        <AlertBox show={notEnoughLayer()} message={t("aeussre.addLayerError")} />
      </Col>
    </Row>
  );
}

export default AussereTable;

function AussereTableRow({ index }) {
  const { t } = useTranslation("translation");
  const dispatch = useDispatch();

  const schichtenInput = useSelector(selectSchichteninput);
  let inputLayer = schichtenInput[index];

  const schichtenCalculated = useSelector(selectSchichtenCalculated);
  let calculatedLayer = schichtenCalculated[index];

  let prevLayer = index >= 1 ? schichtenInput[index - 1] : undefined;
  return (
    <tr>
      {/* Layer name column */}
      <td>
        <input
          type="text"
          className="change-inputTable"
          value={
            inputLayer.schichtName === "1"
              ? `${t("aeussre.layer")} ${index + 1}`
              : inputLayer.schichtName
          }
          name="schichtName"
          onChange={(e) => {
            dispatch(
              updateSchichtenValue({
                index: index,
                schichtName: e.target.value,
              })
            );
          }}
        />
      </td>
      {/* Layer from column */}
      <td>
        <NumberFormatInfInput
          index={index}
          value={inputLayer?.von}
          name="von"
          defaultINF={prevLayer !== undefined ? prevLayer.bis : 0}
          updateValueCallback={function (value) {
            return updateSchichtenValue({
              index: index,
              von: value,
            });
          }}
        />
      </td>

      {/* Layer to column */}
      <td>
        <NumberFormatInfInput
          index={index}
          value={inputLayer?.bis}
          name="bis"
          updateValueCallback={function (value) {
            return updateSchichtenValue({
              index: index,
              bis: value,
            });
          }}
        />
      </td>

      {/* Layer qski column */}
      <td>
        <NumberFormatInfInput
          index={index}
          value={inputLayer?.qski}
          name="qski"
          prefix={""}
          defaultINF={Number.NaN}
          updateValueCallback={function (value) {
            return updateSchichtenValue({
              index: index,
              qski: value,
            });
          }}
        />
      </td>
      {/* Layer a column */}
      <td>
        <NumberFormatInfInput
          index={index}
          value={inputLayer?.a}
          name="a"
          defaultINF={20}
          updateValueCallback={function (value) {
            return updateSchichtenValue({
              index: index,
              a: value,
            });
          }}
        />
      </td>
      {/* Layer rdi column */}
      <td>
        <span name="rdi" className="smallInput">
          <NumberFormatText value={calculatedLayer?.rdi} decimalScale={2} />
        </span>
      </td>
      {/* Layer liZeta column */}
      <td>
        <span name="liZeta" className="smallInput">
          <NumberFormatText value={calculatedLayer?.liZeta} decimalScale={2} />
        </span>
      </td>
      {/* Layer rsdi column */}
      <td>
        <span name="rsdi" className="smallInput">
          <NumberFormatText value={calculatedLayer?.rsdi} decimalScale={2} />
        </span>
      </td>
      {/* Layer action column */}
      <td>
        {index != 0 ? (
          <Button
            variant="danger"
            size="sm"
            onClick={() => dispatch(deleteSchicht({ position: index }))}
          >
            {t("aeussre.removeLayerButton")}
          </Button>
        ) : null}
      </td>
    </tr>
  );
}
